import { computed, observable, makeAutoObservable } from 'mobx';
import { OpenapiProxyStaffDividendsUpdateParamsType } from 'src/api_openapi/generated/openapi_proxy_staff_dividends_update';
import { AppState } from 'src/AppState/AppState';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { timeout } from 'src_common/common/mobx-utils/timeout';
import { MarketModel } from 'src_common/common/websocket2/models/MarketModel';
import { FormConcept } from '../EventResultsSettings/FormConcept';
import { groupDividendsModel, ModelDividendRaw } from './groupDividendsModel';
import { EventId, MarketId } from 'src_common/common/websocket2/id/WebsocketId';

type SelectionSortType = {
    id: number;
    name: string;
};

class MarketView {
    constructor(public readonly marketId: MarketId) {}

    public getMarketModel(): MarketModel | null {
        return this.marketId.getModel();
    }

    public list = (): Array<number> => {
        const marketModel = this.getMarketModel();

        if (marketModel === null) {
            return [];
        }

        const list = [];
        for (const model of marketModel.dividends?.forecast ?? []) {
            list.push(model.id);
        }
        for (const model of marketModel.dividends?.tricast ?? []) {
            list.push(model.id);
        }

        list.sort((a, b): number => {
            return a - b;
        });

        return list;
    };

    getSelections = (): Array<SelectionSortType> => {
        const marketModel = this.marketId.getModel();

        if (marketModel === null) {
            return [];
        }

        const result: Array<SelectionSortType> = [];

        for (const selection of marketModel.selections) {
            // selection.id
            // selection.name
            if (selection.templateId !== 'unnamed-favourite' && selection.metaDataStatus !== 'Non-Runner') {
                result.push({
                    id: selection.id,
                    name: selection.name,
                });
            }

            // .filter(x => x.getIn([ 'template', 'id' ]) !== 'unnamed-favourite')
            // .filter(x => x.getIn([ 'metadata', 'status' ]) !== 'Non-Runner');
        }

        return result;
    };
}

type NewModelType =
    | {
          type: 'forecast';
          newId: number;
      }
    | {
          type: 'tricast';
          newId: number;
      };

class NewModel {
    @observable private newModelInner: null | NewModelType = null;

    constructor(private readonly marketView: MarketView) {
        makeAutoObservable(this);
    }

    @computed private get newModel(): null | NewModelType {
        const newModel = this.newModelInner;

        if (newModel === null) {
            return null;
        }

        const newId = newModel.newId;
        const list = this.marketView.list();

        if (list.includes(newId)) {
            return null;
        }

        return newModel;
    }

    private getNextId(): number {
        const list = this.marketView.list();
        const last = list[list.length - 1];

        if (last !== undefined) {
            return last + 1;
        }

        return 1;
    }

    public add = (type: 'forecast' | 'tricast'): void => {
        this.newModelInner = {
            type,
            newId: this.getNextId(),
        };
    };

    @computed get newId(): number | null {
        const newModel = this.newModel;

        if (newModel === null) {
            return null;
        }

        return newModel.newId;
    }

    @computed get type(): 'forecast' | 'tricast' | null {
        const newModel = this.newModel;

        if (newModel === null) {
            return null;
        }

        return newModel.type;
    }
}

export class ModelDividend {
    @observable public inProgress: boolean = false;
    private inputDividend: FormConcept<string>;
    private inputWinner: FormConcept<number | null>;
    private input2nd: FormConcept<number | null>;
    private input3rd: FormConcept<number | null>;

    // private inProgress: boolean = false;

    public constructor(
        private readonly appState: AppState,
        private readonly eventId: EventId,
        public readonly id: number,
        private readonly getModel: () => ModelDividendRaw | null,
        private readonly marketView: MarketView,
        private readonly newModel: NewModel
    ) {
        this.inputDividend = new FormConcept(() => {
            const dividend = this.getModel()?.dividend;

            if (dividend === undefined) {
                return '';
            }

            return dividend.toString();
        });

        this.inputWinner = new FormConcept(() => {
            return this.getModel()?.winner ?? null;
        });

        this.input2nd = new FormConcept(() => {
            const model = this.getModel();
            if (model === null) {
                return null;
            }

            return model['2nd'];
        });

        this.input3rd = new FormConcept(() => {
            const model = this.getModel();

            if (model === null) {
                return null;
            }

            if (model.type === 'tricast') {
                return model['3rd'];
            }

            return null;
        });
        makeAutoObservable(this);
    }

    @computed get type(): 'forecast' | 'tricast' {
        const newModel = this.newModel;

        if (newModel.newId === this.id) {
            return newModel.type ?? 'forecast';
        }

        const model = this.getModel();
        if (model === null) {
            return 'forecast';
        }

        return model.type;
    }

    @computed get valueDividend(): string {
        return this.inputDividend.value;
    }

    @computed get valueWinner(): string {
        return this.inputWinner.value?.toString() ?? '';
    }

    @computed get value2nd(): string {
        const value = this.input2nd.value?.toString() ?? '';

        if (value !== this.valueWinner) {
            return value;
        }

        return '';
    }

    @computed get value3rd(): string {
        const value = this.input3rd.value?.toString() ?? '';

        if (value !== this.valueWinner && value !== this.value2nd) {
            return value;
        }

        return '';
    }

    public setDividend = (valueText: string): void => {
        this.inputDividend.setValue(valueText);
    };

    public setWinner = (valueText: string): void => {
        const value = parseInt(valueText, 10);
        if (isNaN(value)) {
            console.error('setWinner: expect number');
        } else {
            this.inputWinner.setValue(value);
        }
    };

    public set2nd = (valueText: string): void => {
        const value = parseInt(valueText, 10);
        if (isNaN(value)) {
            console.error('set2nd: expect number');
        } else {
            this.input2nd.setValue(value);
        }
    };

    public set3rd = (valueText: string): void => {
        const value = parseInt(valueText, 10);
        if (isNaN(value)) {
            console.error('set3rd: expect number');
        } else {
            this.input3rd.setValue(value);
        }
    };

    @computed get selectionsWinner(): Array<SelectionSortType> {
        return this.marketView.getSelections();
    }

    @computed get selections2nd(): Array<SelectionSortType> {
        return this.selectionsWinner.filter((item) => {
            return item.id.toString() !== this.valueWinner;
        });
    }

    @computed get selections3rd(): Array<SelectionSortType> {
        return this.selections2nd.filter((item) => {
            return item.id.toString() !== this.value2nd;
        });
    }

    @computed get isNew(): boolean {
        if (this.newModel.newId === this.id) {
            return true;
        }

        return false;
    }

    @computed get buttonLabel(): string {
        return this.isNew ? 'Add' : 'Save';
    }

    private getDividend = (): number | string => {
        const value = parseFloat(this.inputDividend.value);

        if (isNaN(value)) {
            return 'dividend: Value must be a number';
        }
        return value;
    };

    private getDataForecast = (): OpenapiProxyStaffDividendsUpdateParamsType['requestBody'] | string => {
        const dividend = this.getDividend();

        if (typeof dividend === 'string') {
            return dividend;
        }

        const value2nd = this.input2nd.value;

        if (value2nd === null) {
            return 'field "2nd" - expected number';
        }

        const valueWinner = this.inputWinner.value;

        if (valueWinner === null) {
            return 'field "winner" - expected number';
        }

        return {
            id: this.id,
            dividend,
            winner: valueWinner,
            '2nd': value2nd,
            '3rd': null,
        };
    };

    private getDataTricast = (): OpenapiProxyStaffDividendsUpdateParamsType['requestBody'] | string => {
        const value3rd = this.input3rd.value;

        const dataForecast = this.getDataForecast();

        if (typeof dataForecast === 'string') {
            return dataForecast;
        }

        if (value3rd === null) {
            return 'field "3rd" - expected number';
        }

        return {
            id: this.id,
            dividend: dataForecast.dividend,
            winner: dataForecast.winner,
            '2nd': dataForecast['2nd'],
            '3rd': value3rd,
        };
    };

    private getData = (): OpenapiProxyStaffDividendsUpdateParamsType['requestBody'] | string => {
        if (this.type === 'tricast') {
            return this.getDataTricast();
        } else {
            return this.getDataForecast();
        }
    };

    private isChangeForecast = (): boolean => {
        return this.inputDividend.wasChange || this.inputWinner.wasChange || this.input2nd.wasChange;
    };

    private isChangeTricast = (): boolean => {
        return this.isChangeForecast() || this.input3rd.wasChange;
    };

    public get isChange(): boolean {
        if (this.isNew) {
            return true;
        } else {
            if (this.type === 'tricast') {
                return this.isChangeTricast();
            } else {
                return this.isChangeForecast();
            }
        }
    }

    private resetInputs = (): void => {
        this.inputWinner.reset();
        this.input2nd.reset();
        this.input3rd.reset();
        this.inputDividend.reset();
    };

    public saveOrAdd = async (): Promise<void> => {
        if (this.inProgress) {
            console.warn('saveOrAdd - ignore action - request currently in progress');
            return;
        }

        const data = this.getData();

        if (typeof data === 'string') {
            this.appState.reduxAction.addError(data);
            return;
        }

        this.inProgress = true;

        try {
            if (this.isNew) {
                const response = await this.appState.sdkStaff.staff_selection_dividends_add({
                    event_id: this.eventId.toOldId(),
                    market_id: this.marketView.marketId.toOldId(),
                    requestBody: data,
                });

                if (this.appState.reduxAction.addErrorEventChange(response)) {
                    this.inProgress = false;
                    return;
                }
            } else {
                const response = await this.appState.sdkStaff.staff_selection_dividends_update({
                    event_id: this.eventId.toOldId(),
                    market_id: this.marketView.marketId.toOldId(),
                    id: this.id,
                    requestBody: data,
                });

                if (this.appState.reduxAction.addErrorEventChange(response)) {
                    this.inProgress = false;
                    return;
                }
            }

            await this.appState.sdkStaff.triggerUpdateMarket(this.marketView.marketId);
        } catch (error) {
            console.error(error);
            this.inProgress = false;
            return;
        }

        await timeout(1500);

        this.inProgress = false;
        this.resetInputs();
    };
}

export class DividendsSettingsState {
    private readonly appState: AppState;
    private readonly models: MobxMapAutoNew<number, ModelDividend>;

    private readonly marketView: MarketView;
    private readonly newModel: NewModel;

    public constructor(
        private readonly eventId: EventId,
        private readonly marketId: MarketId
    ) {
        this.appState = $appState;

        this.marketView = new MarketView(marketId);

        this.newModel = new NewModel(this.marketView);

        this.models = new MobxMapAutoNew((id: number): ModelDividend => {
            const getModel = (): ModelDividendRaw | null => groupDividendsModel(this.marketId, id);

            return new ModelDividend(this.appState, this.eventId, id, getModel, this.marketView, this.newModel);
        });
    }

    get forecastsOffered(): boolean {
        return this.marketView.getMarketModel()?.forecastsOffered ?? false;
    }

    get tricastsOffered(): boolean {
        return this.marketView.getMarketModel()?.tricastsOffered ?? false;
    }

    listForView(): Array<ModelDividend> {
        const results = this.marketView.list();

        const newModelId = this.newModel.newId;

        if (newModelId !== null) {
            results.push(newModelId);
        }

        return results.map((id) => this.models.get(id));
    }

    public add = (type: 'forecast' | 'tricast'): void => {
        this.newModel.add(type);
    };
}
